import { useState } from "react";
import type { SyntheticEvent } from "react";

import { cn } from "@/lib/utils";

const SAMPLE_SIZE = 64;
const MIN_TRANSPARENT_SHARE = 0.1;
const MIN_LIGHT_SHARE = 0.8;
const LIGHT_LUMA = 204;
const INVISIBLE_ALPHA = 16;
const MAX_REMEMBERED = 300;


/** Blob URLs are unique per file load, so a remembered answer never goes stale. */
const remembered = new Map<string, boolean>();

/**
 * True when RGBA pixels are a light image on a transparent background.
 * Each pixel counts by how visible it is, so thin soft edges still count.
 */
export function isLightOnTransparent(pixels: Uint8ClampedArray): boolean {
  const pixelCount = pixels.length / 4;
  let transparentCount = 0;
  let visibleWeight = 0;
  let lightWeight = 0;
  for (let index = 0; index < pixels.length; index += 4) {
    const alpha = pixels[index + 3];
    if (alpha < INVISIBLE_ALPHA) {
      transparentCount += 1;
      continue;
    }
    visibleWeight += alpha;
    const luma =
      0.2126 * pixels[index] +
      0.7152 * pixels[index + 1] +
      0.0722 * pixels[index + 2];
    if (luma >= LIGHT_LUMA) {
      lightWeight += alpha;
    }
  }
  if (visibleWeight === 0) {
    return false;
  }
  return (
    transparentCount / pixelCount >= MIN_TRANSPARENT_SHARE &&
    lightWeight / visibleWeight >= MIN_LIGHT_SHARE
  );
}

/** False when the browser cannot read the image, which leaves it as it was. */
function readIsLightOnTransparent(image: HTMLImageElement): boolean {
  try {
    const canvas = document.createElement("canvas");
    // An SVG with no size of its own reports 0: sample it at a fixed size.
    const longSide = Math.max(image.naturalWidth, image.naturalHeight);
    const scale = longSide > 0 ? Math.min(1, SAMPLE_SIZE / longSide) : 0;
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    if (longSide === 0) {
      canvas.width = SAMPLE_SIZE;
      canvas.height = SAMPLE_SIZE;
    }
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) {
      return false;
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    return isLightOnTransparent(data);
  } catch {
    return false;
  }
}

function remember(src: string, isLight: boolean) {
  if (remembered.size >= MAX_REMEMBERED) {
    remembered.clear();
  }
  remembered.set(src, isLight);
}

/**
 * Adds a dark tile to an image's classes. Dark in both themes, since `foreground`
 * alone turns light in dark mode. Corners and padding are defaults the caller's
 * own classes override; the background always wins.
 */
export function withDarkTile(className?: string): string {
  return cn("rounded-md p-1.5", className, "bg-foreground dark:bg-muted");
}

/**
 * Reads an image once it loads and, when it is light on transparent, returns
 * `hasDarkTile` true. Pass `onLoad` to the `<img>`.
 */
export function useDarkImageTile(src: string | null | undefined) {
  const [, setReadSrc] = useState<string | null>(null);

  const onLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const loadedSrc = event.currentTarget.getAttribute("src");
    if (!loadedSrc) {
      return;
    }
    if (!remembered.has(loadedSrc)) {
      remember(loadedSrc, readIsLightOnTransparent(event.currentTarget));
    }
    // Always, so a second copy of an image already read picks up the answer.
    setReadSrc(loadedSrc);
  };

  const hasDarkTile = Boolean(src && remembered.get(src));

  return { hasDarkTile, onLoad };
}
